import {
  ContentLayout,
  Header,
  SpaceBetween,
  Button,
  Table,
  Box,
  StatusIndicator,
  Pagination,
  Select,
  SelectProps,
  FormField,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useSplitPanel } from '../../context/SplitPanelContext';
import { getUsersTableDefinition } from './usersConfigServer';
import { useUserDetails } from 'src/providers/UserProvider';
import { useUserListQuery } from 'src/data/api/orbit-service-ts/user/queries/useUserListQuery';
import { Authorized } from 'src/components/Authorized';
import { UserDetailsV2 } from '@amzn/gbsa-orbit-typescript-client';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ROLES_LABELS } from 'src/contstant/commonLabels';
import { useBusinessUnitsQuery } from 'src/data/api/orbit-service-ts/business-unit/queries/useBusinessUnitsQuery';

const PAGE_SIZE = 10;

// Role filter options
const ROLE_OPTIONS: SelectProps.Options = [
  { value: '', label: 'All Roles' },
  ...Object.entries(ROLES_LABELS).map(([value, label]) => ({ value, label })),
];

// Status filter options
const STATUS_OPTIONS: SelectProps.Options = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// Main users management page component
export default function UsersPageServer() {
  // Split panel controls for user details view
  const { setContent, setTitle, closeView, setPanelSize } = useSplitPanel();
  const [selectedItems, setSelectedItems] = useState<UserDetailsV2[]>([]);
  const [tokens, setTokens] = useState<(string | undefined)[]>([undefined]);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  // Filter states
  const { userPreferences } = useUserDetails();
  const [selectedRole, setSelectedRole] = useState<SelectProps.Option | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<SelectProps.Option | null>(null);
  const [selectedBusinessUnit, setSelectedBusinessUnit] =
    useState<SelectProps.Option | null>({
      value: userPreferences.profileSettings.businessUnit,
      label: userPreferences.profileSettings.businessUnit,
    });

  const currentToken = tokens[currentPageIndex - 1];

  // Fetch business units
  const { data: businessUnitOptions, isLoading: isLoadingBusinessUnits } =
    useBusinessUnitsQuery(
      {
        programId: userPreferences.programId,
        isActive: true,
        isArchived: false,
        // we need to do token based pagination here
        nextToken: undefined,
      },
      {
        select: (data) =>
          data.businessUnits.map((bu) => ({
            value: bu.businessUnitId,
            label: bu.businessUnitId,
          })),
      },
    );

  // Reset pagination when filters change
  const resetPagination = () => {
    setTokens([undefined]);
    setCurrentPageIndex(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedRole(null);
    setSelectedStatus(null);
    setSelectedBusinessUnit(null);
    resetPagination();
  };

  // Check if any filter is active
  const hasActiveFilters =
    selectedRole?.value || selectedStatus?.value || selectedBusinessUnit?.value;

  const {
    data: usersList,
    refetch: refetchUsersList,
    isLoading: isLoadingUsersList,
    isFetching: isFetchingUsersList,
  } = useUserListQuery(
    {
      programId: userPreferences.programId,
      maxResults: PAGE_SIZE,
      nextToken: currentToken,
      businessUnit: selectedBusinessUnit?.value || undefined,
      division: undefined,
      isArchived: undefined,
      isUserActive: selectedStatus?.value ? selectedStatus.value === 'true' : undefined,
      roleId: selectedRole?.value || undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );
  const handlePageChange = (pageIndex: number) => {
    if (pageIndex > tokens.length && usersList?.nextToken) {
      setTokens((prev) => [...prev, usersList.nextToken]);
    }
    setCurrentPageIndex(pageIndex);
  };
  // Initialize data and permissions on component mount
  useEffect(() => {
    return () => {
      closeView();
    };
  }, []);

  // Table header with action buttons
  const tableHeader = (
    <Header
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            data-testid="refresh-button"
            iconName="refresh"
            onClick={() => {
              refetchUsersList();
            }}
          />

          <Authorized permissionsToCheck={['EditUser']}>
            <Button variant="normal" data-testid="add-user-button">
              Create User
            </Button>
          </Authorized>
        </SpaceBetween>
      }
    >
      Users
    </Header>
  );
  const hasNextPage = !!usersList?.nextToken;
  return (
    <ContentLayout>
      <Box margin={{ top: 's' }}>
        <Table
          data-testid="users-table"
          columnDefinitions={getUsersTableDefinition()}
          items={usersList?.users ?? []}
          selectedItems={selectedItems}
          selectionType="single"
          loading={isLoadingUsersList}
          loadingText="Loading Users..."
          header={tableHeader}
          filter={
            <SpaceBetween direction="horizontal" size="m">
              <FormField label="Role">
                <Select
                  data-testid="role-filter"
                  placeholder="Select role"
                  selectedOption={selectedRole}
                  onChange={({ detail }) => {
                    setSelectedRole(detail.selectedOption);
                    resetPagination();
                  }}
                  options={ROLE_OPTIONS}
                />
              </FormField>
              <FormField label="Status">
                <Select
                  data-testid="status-filter"
                  placeholder="Select status"
                  selectedOption={selectedStatus}
                  onChange={({ detail }) => {
                    setSelectedStatus(detail.selectedOption);
                    resetPagination();
                  }}
                  options={STATUS_OPTIONS}
                />
              </FormField>
              <FormField label="Business Unit">
                <Select
                  data-testid="business-unit-filter"
                  placeholder="Select business unit"
                  selectedOption={selectedBusinessUnit}
                  onChange={({ detail }) => {
                    setSelectedBusinessUnit(detail.selectedOption);
                    resetPagination();
                  }}
                  options={businessUnitOptions}
                  loadingText="Loading business units"
                  statusType={isLoadingBusinessUnits ? 'loading' : 'finished'}
                />
              </FormField>
              {hasActiveFilters && (
                <Box padding={{ top: 'l' }}>
                  <Button
                    data-testid="clear-filters-button"
                    onClick={clearFilters}
                    variant="link"
                  >
                    Clear filters
                  </Button>
                </Box>
              )}
            </SpaceBetween>
          }
          pagination={
            <Pagination
              disabled={isLoadingUsersList || isFetchingUsersList}
              currentPageIndex={currentPageIndex}
              pagesCount={hasNextPage ? currentPageIndex + 1 : tokens.length}
              openEnd={hasNextPage}
              onChange={({ detail }) => handlePageChange(detail.currentPageIndex)}
            />
          }
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No Users.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </Box>
    </ContentLayout>
  );
}
